// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson

import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { useApp } from '../../store/AppContext.js';
import { parseQif, parseQifRaw } from '../../lib/import/qif-parser.js';
import { detectDuplicates } from '../../lib/import/duplicate-detector.js';
import { applyPayeeSuggestions } from '../../lib/import/payee-matcher.js';
import { buildTransactionPayload, findImbalanceAccount } from '../../lib/import/pipeline.js';
import type { ReviewedTransaction } from '../../lib/import/types.js';
import { buildEvent, validateSplitsBalance } from '../../lib/eventLog.js';
import { useEncryptAndPush } from '../../hooks/useEncryptAndPush.js';
import { ImportReviewTable } from './ImportReviewTable.js';

interface Props {
  worker: Worker | null;
  keyData: ArrayBuffer | null;
  preselectedAccountId?: string;
}

type Step = 'select' | 'map' | 'review' | 'done';

const selectCls = 'w-full bg-bg border border-border text-text text-sm px-3 py-2 focus:border-accent focus:outline-none';
const backBtnCls = 'px-4 py-2 border border-border text-sm text-text-muted hover:bg-bg-elevated transition-colors';
const primaryBtnCls = 'px-4 py-2 bg-accent text-white text-sm hover:bg-accent-hover disabled:opacity-50 transition-colors';

export function QifImport({ worker, keyData, preselectedAccountId }: Props) {
  const { t } = useTranslation(['import', 'common', 'errors']);
  const { state } = useApp();
  const { ledger } = state;
  const navigate = useNavigate();
  const encryptAndPush = useEncryptAndPush(worker, keyData);

  const [step, setStep] = useState<Step>('select');
  const [fileName, setFileName] = useState('');
  const [fileContent, setFileContent] = useState('');
  const [defaultAccountId, setDefaultAccountId] = useState(preselectedAccountId ?? '');
  const [qifAccountNames, setQifAccountNames] = useState<string[]>([]);
  const [accountMapping, setAccountMapping] = useState<Record<string, string>>({});
  const [reviewed, setReviewed] = useState<ReviewedTransaction[]>([]);
  const [importing, setImporting] = useState(false);
  const [importedCount, setImportedCount] = useState(0);
  const [error, setError] = useState('');

  const accounts = [...ledger.accounts.values()].filter((a) => !a.hidden && !a.placeholder);

  const handleFile = useCallback((file: File) => {
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      setFileContent(content);
      const raw = parseQifRaw(content);
      const names = raw.accountSections
        .map((s) => s.account?.name)
        .filter((n): n is string => n !== undefined);
      setQifAccountNames(names);
      setStep('map');
    };
    reader.readAsText(file);
  }, []);

  const handleFilePick = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const handleParse = () => {
    if (!defaultAccountId && qifAccountNames.length === 0) {
      setError(t('selectTargetAccount'));
      return;
    }
    setError('');
    const result = parseQif(fileContent, { accountMapping, defaultAccountId });
    if (result.errors.length > 0 && result.transactions.length === 0) {
      setError(result.errors[0]?.message ?? t('parseError'));
      return;
    }
    const withDup = detectDuplicates(result.transactions, ledger);
    setReviewed(applyPayeeSuggestions(withDup, ledger));
    setStep('review');
  };

  const handleImport = async () => {
    setImporting(true);
    setError('');
    const sessionId = uuidv4();
    const imbalanceAccountId = findImbalanceAccount(ledger, 'USD') ?? defaultAccountId;
    const toImport = reviewed.filter((t) => t.selected);
    const nextSeq = ledger.lastSequence + 1;
    let count = 0;

    for (let i = 0; i < toImport.length; i++) {
      const tx = toImport[i]!;
      try {
        const payload = buildTransactionPayload(tx, sessionId, ledger, imbalanceAccountId);
        validateSplitsBalance(payload.splits);
        const event = await buildEvent('TransactionPosted', payload, nextSeq + i);
        await encryptAndPush(event);
        count++;
        setImportedCount(count);
      } catch (err) {
        setError(t('errorOnTransaction', { index: i + 1, message: err instanceof Error ? err.message : String(err) }));
        setImporting(false);
        return;
      }
    }
    setImporting(false);
    setStep('done');
  };

  if (step === 'select') {
    return (
      <div
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
        className="border-2 border-dashed border-border p-12 text-center hover:border-accent transition-colors"
      >
        <p className="font-mono text-2xl text-accent mb-3">QIF</p>
        <p className="text-text-muted text-sm mb-4">{t('qif.dropZone')}</p>
        <label className={`${primaryBtnCls} cursor-pointer`}>
          {t('qif.chooseFile')}
          <input type="file" accept=".qif" className="hidden" onChange={handleFilePick} />
        </label>
      </div>
    );
  }

  if (step === 'map') {
    return (
      <div className="space-y-6">
        <div className="text-xs text-text-muted">{t('csv.file')} <span className="font-mono text-text">{fileName}</span></div>

        <div>
          <label className="block text-xs text-text-muted mb-1">{t('qif.defaultTarget')}</label>
          <select value={defaultAccountId} onChange={(e) => setDefaultAccountId(e.target.value)} className={selectCls}>
            <option value="">{t('selectAccount')}</option>
            {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
          <p className="text-xs text-text-subtle mt-1">{t('qif.defaultTargetNote')}</p>
        </div>

        {qifAccountNames.length > 0 && (
          <div>
            <p className="text-xs text-text-muted mb-2 uppercase tracking-wide">{t('qif.mapAccounts')}</p>
            <div className="space-y-2">
              {qifAccountNames.map((name) => (
                <div key={name} className="flex items-center gap-3">
                  <span className="text-sm text-text-muted w-48 truncate font-mono">{name}</span>
                  <span className="text-text-subtle">→</span>
                  <select
                    value={accountMapping[name] ?? ''}
                    onChange={(e) => setAccountMapping((m) => ({ ...m, [name]: e.target.value }))}
                    className="flex-1 bg-bg border border-border text-text text-sm px-2 py-1 focus:border-accent focus:outline-none"
                  >
                    <option value="">{t('qif.useDefault')}</option>
                    {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                  </select>
                </div>
              ))}
            </div>
          </div>
        )}

        {error && <p className="text-negative text-sm">{error}</p>}

        <div className="flex gap-3">
          <button onClick={() => setStep('select')} className={backBtnCls}>{t('common:actions.back')}</button>
          <button onClick={handleParse} className={primaryBtnCls}>{t('ofx.previewImport')}</button>
        </div>
      </div>
    );
  }

  if (step === 'review') {
    const toImport = reviewed.filter((t) => t.selected);
    return (
      <div className="space-y-4">
        <ImportReviewTable transactions={reviewed} accounts={accounts} onChange={setReviewed} />
        {error && <p className="text-negative text-sm">{error}</p>}
        <div className="flex gap-3">
          <button onClick={() => setStep('map')} className={backBtnCls}>{t('common:actions.back')}</button>
          <button
            onClick={() => { void handleImport(); }}
            disabled={toImport.length === 0 || importing}
            className={primaryBtnCls}
          >
            {importing ? t('importingProgress', { current: importedCount, total: toImport.length }) : t('importCount', { count: toImport.length })}
          </button>
        </div>
      </div>
    );
  }

  if (step === 'done') {
    return (
      <div className="text-center space-y-4 py-8">
        <p className="font-mono text-positive text-2xl">✓</p>
        <p className="text-md font-medium text-text">{t('importedCount', { count: importedCount })}</p>
        <div className="flex gap-3 justify-center">
          {defaultAccountId && (
            <button onClick={() => navigate(`/accounts/${defaultAccountId}`)} className={primaryBtnCls}>
              {t('viewAccountRegister')}
            </button>
          )}
          <button onClick={() => { setStep('select'); setFileContent(''); setFileName(''); }} className={backBtnCls}>
            {t('importAnotherFile')}
          </button>
        </div>
      </div>
    );
  }

  return null;
}
